import {
    HIDE_LEGACY_REPORTS_PROPERTY_ID,
    PERMISSION_LEVEL_PROPERTY_ID,
    QUICK_VIEW_ONLY_PROPERTY_ID,
    clampPermissionLevel,
    isReportsV2Enabled
} from "containers/admin/reportsV2ConfigUtils";

/**
 * Shared logic for the Reports V2 migration notification (REV-17023).
 *
 * Two surfaces reuse this module:
 *   - ReportsMigrationModal  (first-login, once per user/org)
 *   - ReportsMigrationBanner (persistent, not dismissible)
 *   - ReportsV2Page → ReportProvider legacyReportsAccess (REV-17820)
 *
 * Migration modal/banner targeting derives from existing signals: the org
 * still has legacy MicroStrategy reporting access (reporting not disabled),
 * Reports V2 is enabled, and the user meets the same permission-level gate
 * used by the Reports V2 menu item. New / Reports V2-only accounts
 * (reporting disabled) never qualify.
 *
 * Hiding legacy reports from the left nav (REV-17820) additionally requires
 * `properties/hide-legacy-reports` — see `shouldHideLegacyReports`.
 */

// Sunset dates shared across migration surfaces so copy stays in sync.
export const MIGRATION_TIMELINE = {
    legacyRemovalDate: "October 15",
    legacyNavSunsetDate: "October 15, 2026"
};

const REPORTS_V2_PATH = (orgId) => `/${orgId}/reports-v2`;
const LEGACY_REPORTS_PATH = (orgId) => `/${orgId}/mstr-native-report-parent`;

export function reportsV2Path(orgId) {
    return REPORTS_V2_PATH(orgId);
}

export function legacyReportsPath(orgId) {
    return LEGACY_REPORTS_PATH(orgId);
}

function findConfig(orgConfigs, id) {
    return Array.isArray(orgConfigs)
        ? orgConfigs.find((cfg) => cfg && cfg.id === id)
        : undefined;
}

function isTruthyConfigValue(value) {
    return value === true || value === "true";
}

export function isHideLegacyReportsEnabled(orgConfigs) {
    const cfg = findConfig(orgConfigs, HIDE_LEGACY_REPORTS_PROPERTY_ID);
    return Boolean(cfg && isTruthyConfigValue(cfg.value));
}

export { isReportsV2Enabled };

export function isQuickViewOnlyEnabled(orgConfigs) {
    const cfg = findConfig(orgConfigs, QUICK_VIEW_ONLY_PROPERTY_ID);
    return Boolean(cfg && isTruthyConfigValue(cfg.value));
}

/**
 * Quick-View-only mode (REV-17800). Reporting being disabled wins: the legacy
 * route is not registered at all in that case (see routes.js), so the nav
 * entry would be a dead link.
 */
export function shouldRestrictToQuickView(currentOrganization, orgConfigs) {
    if (!currentOrganization || currentOrganization.disableReporting) {
        return false;
    }
    return isQuickViewOnlyEnabled(orgConfigs);
}

// Tabs kept on the legacy page in Quick-View-only mode. Named-query tabs are
// named by the backend (`reportTab`), so they are matched on report type
// rather than by group name.
export const QUICK_VIEW_ONLY_GROUPS = ["Quick View", "Exported Data"];
export const NAMED_QUERY_REPORT_TYPE = "NamedQuery Reports";

export function isQuickViewOnlyReport(report) {
    if (!report) return false;
    return (
        QUICK_VIEW_ONLY_GROUPS.includes(report.group) ||
        report.reportType === NAMED_QUERY_REPORT_TYPE
    );
}

export function getReportsV2RequiredLevel(orgConfigs) {
    const cfg = findConfig(orgConfigs, PERMISSION_LEVEL_PROPERTY_ID);
    return clampPermissionLevel(cfg && cfg.value);
}

export function hasReportsV2Permission(currentUser, orgConfigs) {
    return Boolean(
        currentUser &&
            currentUser.superAdminLevel >= getReportsV2RequiredLevel(orgConfigs)
    );
}

/**
 * Whether the current org/user should be treated as part of the legacy
 * reporting (MSTR) migration cohort.
 */
export function isLegacyReportingMigrationCohort(
    currentOrganization,
    orgConfigs,
    currentUser
) {
    if (!currentOrganization) return false;
    if (currentOrganization.disableReporting) return false;
    return (
        isReportsV2Enabled(orgConfigs) &&
        hasReportsV2Permission(currentUser, orgConfigs)
    );
}

// ---- Persistence helpers -------------------------------------------------
// The authoritative "seen" flag lives in the backend user preferences and is
// surfaced on the redux user via GET /private/user. localStorage is only a
// fast-path cache so we can avoid rendering a flash of the modal on reload.

function seenStorageKey(orgId, userId) {
    return `rev:reportsV2MigrationSeen:${orgId || "default"}:${
        userId || "anon"
    }`;
}

export function isModalSeenLocally(orgId, userId) {
    try {
        return localStorage.getItem(seenStorageKey(orgId, userId)) === "1";
    } catch (e) {
        return false;
    }
}

export function markModalSeenLocally(orgId, userId) {
    try {
        localStorage.setItem(seenStorageKey(orgId, userId), "1");
    } catch (e) {
        // Ignore storage failures (private mode / quota).
    }
}

/** Reads the backend-persisted, per-org flag off the redux user. */
export function isModalSeenForUser(currentUser, orgId) {
    return Boolean(
        currentUser &&
            currentUser.preferences &&
            currentUser.preferences[orgId] &&
            currentUser.preferences[orgId].reportsV2MigrationModalSeen
    );
}

/**
 * Whether legacy reports should be hidden from the left nav by default and
 * exposed via the Reports V2 "View Legacy Reports" link (REV-17820).
 */
export function shouldHideLegacyReports(
    currentOrganization,
    orgConfigs,
    currentUser
) {
    if (!currentOrganization || currentOrganization.disableReporting) {
        return false;
    }
    return (
        isHideLegacyReportsEnabled(orgConfigs) &&
        isReportsV2Enabled(orgConfigs) &&
        hasReportsV2Permission(currentUser, orgConfigs)
    );
}

/**
 * Whether the legacy Reports / Quickview nav item should appear in the left
 * nav. Hidden whenever hideLegacyReports is enabled for the org, except in
 * Quick-View-only mode, which keeps the item (REV-17800).
 */
export function shouldShowLegacyReportsInNav(
    currentOrganization,
    orgConfigs,
    currentUser
) {
    if (!currentOrganization || currentOrganization.disableReporting) {
        return false;
    }
    if (shouldRestrictToQuickView(currentOrganization, orgConfigs)) {
        return true;
    }
    return !shouldHideLegacyReports(
        currentOrganization,
        orgConfigs,
        currentUser
    );
}
